import {
  AUTH_RESTART_ATTEMPTED_KEY,
  COGNITO_REALM_COOKIE,
  COGNITO_REALM_OAUTH,
  REFRESH_TOKEN_MAX_AGE_SECONDS,
} from "@/constants";
import type { AuthRealm } from "@/shared/auth/authRealm";
import { resolveAuthRealm } from "@/shared/auth/authRealm";
import { clearSessionFlag } from "@/shared/auth/sessionFlag";

/** Pre-realm-split cookies/session keys — cleared whenever realm tokens are read or written. */
const LEGACY_ACCESS_TOKEN_COOKIE = "olive_access_token";
const LEGACY_TENANT_ACCESS_TOKEN_COOKIE = "olive_tenant_access_token";
const LEGACY_ADMIN_ACCESS_TOKEN_COOKIE = "olive_admin_access_token";
const LEGACY_REFRESH_TOKEN_COOKIE = "olive_refresh_token";
const LEGACY_PKCE_VERIFIER_KEY = "olive_pkce_verifier";
const LEGACY_AUTH_STATE_KEY = "olive_auth_state";

export type CognitoTokens = {
  id_token: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

// ─── Base64-URL helpers ──────────────────────────────────────────

function base64UrlEncode(buffer: Uint8Array): string {
  let binary = "";
  for (const byte of buffer) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(str: string): Uint8Array {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const full = padded + "=".repeat((4 - (padded.length % 4)) % 4);
  const binary = atob(full);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─── Cookie helpers ──────────────────────────────────────────────

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `max-age=${maxAgeSeconds}`,
    "path=/",
    "SameSite=Lax",
    location.protocol === "https:" ? "Secure" : "",
  ]
    .filter(Boolean)
    .join("; ");
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
}

// ─── PKCE ────────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

// ─── URL builders ────────────────────────────────────────────────

export function buildCognitoLoginUrl(opts: {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  state?: string;
  scope?: string;
  identityProvider?: string;
  prompt?: "login";
}): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: opts.clientId,
    redirect_uri: opts.redirectUri,
    code_challenge_method: "S256",
    code_challenge: opts.codeChallenge,
    scope: opts.scope ?? "openid email profile",
  });
  if (opts.state) {
    params.set("state", opts.state);
  }
  if (opts.identityProvider) {
    params.set("identity_provider", opts.identityProvider);
  }
  if (opts.prompt) {
    params.set("prompt", opts.prompt);
  }
  return `https://${opts.cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Sign-out URL for Cognito's `/logout` endpoint.
 *
 * Send `client_id` + `logout_uri` only. Adding `redirect_uri`/`response_type` switches Cognito to
 * its "sign out, then authorize" mode: it chains into `/oauth2/authorize` forwarding only the
 * params it was given — so with no `code_challenge` it mints a code for a PKCE challenge this app
 * never issued, which `/callback` cannot exchange.
 *
 * `logoutUri` must exactly match a registered sign-out URL on the app client (the tenant
 * provisioner registers the origin *with* a trailing slash).
 */
export function buildCognitoLogoutUrl(opts: {
  cognitoDomain: string;
  clientId: string;
  logoutUri: string;
}): string {
  const params = new URLSearchParams({
    client_id: opts.clientId,
    logout_uri: opts.logoutUri,
  });
  return `https://${opts.cognitoDomain}/logout?${params.toString()}`;
}

// ─── Redirect to Cognito ─────────────────────────────────────────

export async function redirectToCognitoLogin(opts: {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  state?: string;
  identityProvider?: string;
  realm: AuthRealm;
  prompt?: "login";
}): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  sessionStorage.setItem(COGNITO_REALM_OAUTH[opts.realm].PKCE_VERIFIER, verifier);
  if (opts.state) {
    sessionStorage.setItem(COGNITO_REALM_OAUTH[opts.realm].AUTH_STATE, opts.state);
  }

  window.location.assign(
    buildCognitoLoginUrl({ ...opts, codeChallenge: challenge }),
  );
}

// ─── Token exchange ──────────────────────────────────────────────

export async function exchangeCodeForTokens(opts: {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  code: string;
  realm: AuthRealm;
}): Promise<CognitoTokens> {
  const verifier = sessionStorage.getItem(
    COGNITO_REALM_OAUTH[opts.realm].PKCE_VERIFIER,
  );
  if (!verifier) {
    throw new Error("Missing PKCE code verifier.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: opts.clientId,
    redirect_uri: opts.redirectUri,
    code: opts.code,
    code_verifier: verifier,
  });

  const res = await fetch(`https://${opts.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${text}`);
  }

  sessionStorage.removeItem(COGNITO_REALM_OAUTH[opts.realm].PKCE_VERIFIER);
  return (await res.json()) as CognitoTokens;
}

/** Exchange a refresh token for new Cognito tokens (including a new ID token). */
export async function refreshCognitoTokens(opts: {
  cognitoDomain: string;
  clientId: string;
  refreshToken: string;
}): Promise<CognitoTokens> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: opts.clientId,
    refresh_token: opts.refreshToken,
  });

  const res = await fetch(`https://${opts.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token refresh failed (${res.status}): ${text}`);
  }

  const raw = (await res.json()) as Partial<CognitoTokens>;
  if (!raw.id_token) {
    throw new Error("Token refresh response missing id_token.");
  }

  return {
    id_token: raw.id_token,
    access_token: raw.access_token ?? "",
    refresh_token: raw.refresh_token ?? opts.refreshToken,
    token_type: raw.token_type ?? "Bearer",
    expires_in: raw.expires_in ?? 3600,
  };
}

/**
 * Revokes the refresh token at Cognito's OAuth2 revocation endpoint.
 * Invalidates that refresh token and access tokens issued from it.
 *
 * @see https://docs.aws.amazon.com/cognito/latest/developerguide/revocation-endpoint.html
 */
export async function revokeCognitoRefreshToken(opts: {
  cognitoDomain: string;
  clientId: string;
  refreshToken: string;
}): Promise<void> {
  const body = new URLSearchParams({
    token: opts.refreshToken,
    client_id: opts.clientId,
  });

  const res = await fetch(`https://${opts.cognitoDomain}/oauth2/revoke`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token revocation failed (${res.status}): ${text}`);
  }
}

// ─── JWT expiry (decode only; backend validates signatures) ──────

function decodeJwtPart(part: string): Record<string, unknown> {
  const json = new TextDecoder().decode(base64UrlDecode(part));
  return JSON.parse(json) as Record<string, unknown>;
}

/** Returns the JWT `exp` claim in seconds since epoch, or null when missing or malformed. */
export function getJwtExpiryEpochSeconds(jwt: string): number | null {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload = decodeJwtPart(parts[1]);
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

export function isJwtExpired(
  jwt: string,
  nowEpochSeconds = Math.floor(Date.now() / 1000),
): boolean {
  const exp = getJwtExpiryEpochSeconds(jwt);
  if (exp === null) {
    return true;
  }
  return exp <= nowEpochSeconds;
}

/** Refresh ID tokens this many seconds before JWT `exp` (clock skew + network latency). */
export const TOKEN_REFRESH_BUFFER_SECONDS = 60;

/** True when the JWT is expired or will expire within `bufferSeconds`. */
export function isJwtExpiringSoon(
  jwt: string,
  bufferSeconds = TOKEN_REFRESH_BUFFER_SECONDS,
  nowEpochSeconds = Math.floor(Date.now() / 1000),
): boolean {
  const exp = getJwtExpiryEpochSeconds(jwt);
  if (exp === null) {
    return true;
  }
  return exp - nowEpochSeconds <= bufferSeconds;
}

/** RSA public key from a Cognito JWKS document. */
type JwkKey = {
  kid: string;
  kty: string;
  n: string;
  e: string;
  alg?: string;
  use?: string;
};

async function importRsaJwk(jwk: JwkKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    { kty: jwk.kty, n: jwk.n, e: jwk.e, alg: jwk.alg, use: jwk.use },
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    false,
    ["verify"],
  );
}

/**
 * Validate a Cognito ID token:
 *  1. Match the `kid` in the JWT header to a key in the JWKS.
 *  2. Verify the RS256 signature via Web Crypto.
 *  3. Assert standard claims: `iss`, `aud`, `token_use`, `exp`.
 *
 * Returns the decoded payload on success; throws on any failure.
 */
export async function validateIdToken(
  idToken: string,
  opts: {
    userPoolId: string;
    clientId: string;
    jwks: { keys: JwkKey[] };
  },
): Promise<Record<string, unknown>> {
  const parts = idToken.split(".");
  if (parts.length !== 3) {
    throw new Error("Malformed JWT: expected 3 parts.");
  }

  const header = decodeJwtPart(parts[0]) as { kid?: string };
  if (!header.kid) {
    throw new Error("JWT header missing kid.");
  }

  const jwk = opts.jwks.keys.find((k) => k.kid === header.kid);
  if (!jwk) {
    throw new Error("No matching key found in JWKS.");
  }

  const key = await importRsaJwk(jwk);
  const data = new TextEncoder().encode(`${parts[0]}.${parts[1]}`);
  const signature = base64UrlDecode(parts[2]);

  const sigBuf = new Uint8Array(signature).buffer as ArrayBuffer;

  const valid = await crypto.subtle.verify(
    "RSASSA-PKCS1-v1_5",
    key,
    sigBuf,
    data,
  );
  if (!valid) {
    throw new Error("Invalid token signature.");
  }

  const payload = decodeJwtPart(parts[1]);

  const region = opts.userPoolId.split("_")[0];
  const expectedIssuer = `https://cognito-idp.${region}.amazonaws.com/${opts.userPoolId}`;
  if (payload.iss !== expectedIssuer) {
    throw new Error(`Invalid issuer: expected ${expectedIssuer}`);
  }

  if (payload.aud !== opts.clientId) {
    throw new Error("Invalid audience.");
  }

  if (payload.token_use !== "id") {
    throw new Error("Token is not an ID token.");
  }

  const now = Math.floor(Date.now() / 1000);
  if (typeof payload.exp === "number" && payload.exp < now) {
    throw new Error("Token has expired.");
  }

  return payload;
}

// ─── Token storage (cookies, per auth realm) ───────────────────

function realmCookieNames(realm: AuthRealm) {
  return COGNITO_REALM_COOKIE[realm];
}

function clearLegacyTokenCookies(): void {
  deleteCookie(LEGACY_ACCESS_TOKEN_COOKIE);
  deleteCookie(LEGACY_TENANT_ACCESS_TOKEN_COOKIE);
  deleteCookie(LEGACY_ADMIN_ACCESS_TOKEN_COOKIE);
  deleteCookie(LEGACY_REFRESH_TOKEN_COOKIE);
  sessionStorage.removeItem(LEGACY_PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(LEGACY_AUTH_STATE_KEY);
}

/** One-time session bootstrap: clear pre-realm-split cookies and OAuth keys. */
export function bootstrapAuthSession(): void {
  clearLegacyTokenCookies();
}

export function storeTokens(realm: AuthRealm, tokens: CognitoTokens): void {
  const names = realmCookieNames(realm);
  setCookie(names.ID, tokens.id_token, tokens.expires_in);
  setCookie(names.REFRESH, tokens.refresh_token, REFRESH_TOKEN_MAX_AGE_SECONDS);
}

export function readStoredTokens(
  realm: AuthRealm = resolveAuthRealm(),
): {
  idToken: string;
  refreshToken: string;
} | null {
  const names = realmCookieNames(realm);
  const refreshToken = getCookie(names.REFRESH);
  if (!refreshToken) return null;

  const idToken = getCookie(names.ID);
  if (!idToken) return null;
  return { idToken, refreshToken };
}

/**
 * Bearer token sent to Olive API routes for the given auth realm.
 * All endpoints use the Cognito ID token.
 */
export function readApiBearerToken(
  realm: AuthRealm = resolveAuthRealm(),
): string | null {
  return getCookie(realmCookieNames(realm).ID);
}

export function clearStoredTokens(realm: AuthRealm = resolveAuthRealm()): void {
  const names = realmCookieNames(realm);
  deleteCookie(names.ID);
  deleteCookie(names.REFRESH);
  sessionStorage.removeItem(COGNITO_REALM_OAUTH[realm].PKCE_VERIFIER);
  sessionStorage.removeItem(COGNITO_REALM_OAUTH[realm].AUTH_STATE);
  // Every sign-out hands the next sign-in a fresh retry budget. Without this the marker outlives
  // the sign-out and the next failed exchange dead-ends instead of restarting login.
  clearSessionFlag(AUTH_RESTART_ATTEMPTED_KEY);
  clearLegacyTokenCookies();
}

/** Clears auth cookies and OAuth session keys. Preserves post-logout and SSO routing flags. */
export function clearAuthSession(): void {
  clearStoredTokens();
}

// ─── State helpers (for OAuth redirect round-trip) ───────────────

export function hasStoredPkceVerifier(
  realm: AuthRealm = resolveAuthRealm(),
): boolean {
  return (
    sessionStorage.getItem(COGNITO_REALM_OAUTH[realm].PKCE_VERIFIER) !== null
  );
}

export function readStoredState(realm: AuthRealm = resolveAuthRealm()): string | null {
  return sessionStorage.getItem(COGNITO_REALM_OAUTH[realm].AUTH_STATE);
}

export function clearStoredState(realm: AuthRealm = resolveAuthRealm()): void {
  sessionStorage.removeItem(COGNITO_REALM_OAUTH[realm].AUTH_STATE);
}
